import React, { useMemo } from 'react';
import { DomainTuple, VictoryArea, VictoryLine } from 'victory';

import { colors } from '../../../../colors';
import { Band, ZoningBand } from '../../../../screens/ProfitCalculations/types';
import { isZoningBand } from '..';

interface Props {
  band: Band | ZoningBand | undefined;
  domain:
    | {
        x: DomainTuple;
        y: DomainTuple;
      }
    | undefined;
}

const BandChart: React.FC<Props> = (props: Props) => {
  const { band, domain } = props;

  const bollingerBandMiddleLine = useMemo(() => {
    if (!band?.middleBand?.length) {
      return undefined;
    }

    const data = band.middleBand;

    const range =
      data[data.length - 1].timestamp - data[data.length - 2].timestamp;

    const dataPoins = data.map((b) => {
      return { x: b.timestamp - range, y: b.value };
    });

    return dataPoins;
  }, [band?.middleBand]);

  const bollingerBandLowerLine = useMemo(() => {
    if (!band?.lowerBand?.length) {
      return undefined;
    }

    const data = band.lowerBand;

    const range =
      data[data.length - 1].timestamp - data[data.length - 2].timestamp;

    const dataPoins = data.map((b) => {
      return {
        x: b.timestamp - range,
        y: Math.min(
          domain?.y[1] as number,
          Math.max((domain?.y[0] as number) ?? 0, b.value),
        ),
      };
    });

    return dataPoins;
  }, [band?.lowerBand, domain?.y]);

  const bollingerBandBuyLine = useMemo(() => {
    if (!band) {
      return undefined;
    }

    if (!isZoningBand(band)) {
      return undefined;
    }

    if (!band?.buyBand?.length) {
      return undefined;
    }

    const data = band.buyBand;

    const range =
      data[data.length - 1].timestamp - data[data.length - 2].timestamp;

    const dataPoins = data.map((b) => {
      return {
        x: b.timestamp - range,
        y: Math.max((domain?.y[0] as number) ?? 0, b.value),
      };
    });

    return dataPoins;
  }, [band, domain?.y]);

  const bollingerAreaBand = useMemo(() => {
    if (!band?.upperBand?.length || !band?.lowerBand?.length) {
      return undefined;
    }

    const data = band.upperBand;

    const range =
      data[data.length - 1].timestamp - data[data.length - 2].timestamp;

    const dataPoins = data.map((b, i) => {
      return {
        x: b.timestamp - range,
        y: b.value,
        y0: band.lowerBand[i].value,
      };
    });

    return dataPoins;
  }, [band?.lowerBand, band?.upperBand]);

  const bollingerSellAreaBand = useMemo(() => {
    if (!band) {
      return undefined;
    }

    if (!isZoningBand(band)) {
      return undefined;
    }

    if (!band?.sellBand?.length || !band?.upperBand?.length) {
      return undefined;
    }

    const data = band.sellBand;

    const range =
      data[data.length - 1].timestamp - data[data.length - 2].timestamp;

    const dataPoins = data.map((b, i) => {
      return {
        x: b.timestamp - range,
        y: b.value,
        y0: band.upperBand[i].value,
      };
    });

    return dataPoins;
  }, [band]);

  const bollingerBuyAreaBand = useMemo(() => {
    if (!band) {
      return undefined;
    }

    if (!isZoningBand(band)) {
      return undefined;
    }

    if (!band?.buyBand?.length || !band?.lowerBand?.length) {
      return undefined;
    }

    const data = band.lowerBand;

    const range =
      data[data.length - 1].timestamp - data[data.length - 2].timestamp;

    const dataPoins = data.map((b, i) => {
      return {
        x: b.timestamp - range,
        y: Math.max((domain?.y[0] as number) ?? 0, b.value),
        y0: Math.max((domain?.y[0] as number) ?? 0, band.buyBand[i].value),
      };
    });

    return dataPoins;
  }, [band, domain?.y]);

  if (!band) {
    return null;
  }

  return (
    <g>
      {bollingerSellAreaBand && (
        <VictoryArea
          {...props}
          data={bollingerSellAreaBand}
          style={{
            data: {
              fill: `${colors.red}25`,
              stroke: `${colors.red}75`,
            },
          }}
        />
      )}

      {bollingerAreaBand && (
        <VictoryArea
          {...props}
          data={bollingerAreaBand}
          style={{
            data: {
              fill: `${colors.ligthBlue}25`,
              stroke: `${colors.ligthBlue}75`,
            },
          }}
        />
      )}

      {bollingerBuyAreaBand && (
        <VictoryArea
          {...props}
          data={bollingerBuyAreaBand}
          style={{
            data: {
              fill: `${colors.green}25`,
              stroke: `${colors.green}75`,
            },
          }}
        />
      )}

      {bollingerBandMiddleLine && (
        <VictoryLine
          {...props}
          data={bollingerBandMiddleLine}
          style={{
            data: {
              stroke: colors.orange,
              strokeWidth: 1,
              opacity: 0.75,
            },
          }}
        />
      )}

      {bollingerBandLowerLine && (
        <VictoryLine
          {...props}
          data={bollingerBandLowerLine}
          style={{
            data: {
              stroke: `${colors.ligthBlue}75`,
              strokeWidth: 1,
            },
          }}
        />
      )}

      {bollingerBandBuyLine && (
        <VictoryLine
          {...props}
          data={bollingerBandBuyLine}
          style={{
            data: {
              stroke: `${colors.green}75`,
              strokeWidth: 1,
            },
          }}
        />
      )}
    </g>
  );
};

export default React.memo(BandChart);
